import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { NgbProgressbarModule } from '@ng-bootstrap/ng-bootstrap';
import { addDays, DateOnly, TreatmentPlanSettings } from '../../core';
import { BaseComponent } from '../base';
import { ConvertDatePipe } from '../pipe';


/**
 * Component for published plans progress bars.
 */
@Component({
	selector: 'tsp-published-plans-progress',
	imports: [
		ConvertDatePipe,
		NgbProgressbarModule
	],
	templateUrl: './published-plans-progress.component.html',
	changeDetection: ChangeDetectionStrategy.Eager
})
export class PublishedPlansProgressComponent extends BaseComponent {

	publishedPlans = input.required<TreatmentPlanSettings[] | undefined>();

	items = computed(() => this._updateProgress([...this.publishedPlans() ?? []]));

	today = computed(() => {
		const date = new DateOnly();
		date.setHours(0);
		date.setMinutes(0);
		date.setSeconds(0);
		return date;
	});

	readonly MAX = 100;

	private _updateProgress(list: TreatmentPlanSettings[]): ProgressItem[] {
		const items = list?.map(plan => this._progressItemMapper(plan)) ?? [];
		const max = Math.max(...items.map(item => item.init + item.latency + item.correction + item.consolidation));
		items.forEach(i => {
			i.init = this._getBarValue(i.init, max);
			i.latency = this._getBarValue(i.latency, max);
			i.correction = this._getBarValue(i.correction, max);
			i.consolidation = this._getBarValue(i.consolidation, max);
		});
		return items;
	}

	private _progressItemMapper(plan: TreatmentPlanSettings): ProgressItem {
		let init, latency, correction, consolidation;
		init = latency = correction = consolidation = 0;
		const consolidationDays = plan.settings?.consolidationDays ?? 0;
		const startConsDate = addDays(plan.finalTreatmentDate, 1);
		const consolidationDate = plan.consolidationDate ? new DateOnly(plan.consolidationDate) : addDays(startConsDate, consolidationDays);
		const startDate = new DateOnly(plan.startTreatmentDate);
		const surgeryDate = new DateOnly(plan.surgeryDate);
		const completed = this.today() >= consolidationDate;
		if (!completed) {
			if (this.today() >= startConsDate) {
				consolidation = this._getDaysDiff(this.today(), consolidationDate);
			} else if (this.today() >= startDate) {
				consolidation = consolidationDays;
				correction = this._getDaysDiff(this.today(), startConsDate);
			} else if (this.today() >= surgeryDate) {
				consolidation = consolidationDays;
				correction = this._getDaysDiff(startConsDate, startDate);
				latency = this._getDaysDiff(this.today(), startDate);
			} else {
				consolidation = consolidationDays;
				correction = this._getDaysDiff(startConsDate, startDate);;
				latency = plan.latencyDays;
				init = this._getDaysDiff(this.today(), surgeryDate);
			}
		}
		return {
			...plan,
			latencyDays: latency,
			treatmentDays: correction,
			consolidationDays: consolidation,
			init,
			latency,
			correction,
			consolidation,
			completed
		};
	}

	private _getDaysDiff(date1: Date, date2: Date): number {
		return Math.abs(Math.round((date1.getTime() - date2.getTime()) / 86400000));
	}

	private _getBarValue(val: number, max: number): number {
		return Math.ceil(val * 100 / max);
	}

}

interface ProgressItem extends TreatmentPlanSettings {
	treatmentDays: number;
	consolidationDays: number;
	init: number;
	latency: number;
	correction: number;
	consolidation: number;
	completed: boolean;
}
